import { SignatureV4 } from '@smithy/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@smithy/protocol-http';
import { splitQuery } from './query';
import { getAWSCredentials } from './Auth';

/**
 * Builds AWS SigV4 headers for API Gateway requests.
 *
 * Expects a baseURL (including protocol/host and optional stage path) and a URL
 * (path + optional query) relative to that base for signing.
 *
 * @param {{
 *   region: string,
 *   baseURL: string,
 *   url?: string,
 *   method?: string,
 *   headers?: Record<string, string>,
 *   data?: any
 * }} config
 * @returns {Promise<Record<string, string>>} Signed headers to merge into the request.
 */
export const getAwsAuthHeader = async (config) => {
  if (!config?.region) {
    throw new Error('getAwsAuthHeader: region is required');
  }
  if (!config?.baseURL) {
    throw new Error('getAwsAuthHeader: baseURL is required');
  }

  const currentCredentials = await getAWSCredentials();
  const credentials = {
    accessKeyId: currentCredentials.accessKeyId,
    secretAccessKey: currentCredentials.secretAccessKey,
    sessionToken: currentCredentials.sessionToken
  };

  const base = new URL(config.baseURL);
  const host = base.host;
  const protocol = base.protocol; // e.g. 'https:'

  const signer = new SignatureV4({
    service: 'execute-api',
    region: config.region,
    credentials,
    sha256: Sha256,
    applyChecksum: false
  });

  // Extract path and query from config.url using helper
  const { path: pathOnly, query: queryBag } = splitQuery(config.url || '/');

  const method = (config.method || 'GET').toUpperCase();
  const request = new HttpRequest({
    method,
    headers: {
      host,
      'bs-token': config.headers?.['bs-token']
    },
    hostname: host,
    path: pathOnly,
    query: queryBag,
    body: config.data,
    protocol
  });

  const signed = await signer.sign(request);
  return signed.headers;
};

export const signUrl = async (url) => {
  return url;
};
